import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { FaDoorOpen, FaTimes, FaCheck, FaExternalLinkAlt, FaVideo } from 'react-icons/fa';
import { useOverlay } from '../../contexts/FocusContext';

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
}

interface RoomSelectorProps {
  accessToken: string;
  eventId: string;
  currentRoomId: string;
  onRoomChange: (room: Room) => void;
  cachedRooms?: Room[]; // Use cached rooms from parent
  isLoadingRooms?: boolean; // Loading state from parent
}

export interface RoomSelectorRef {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const RoomSelector = forwardRef<RoomSelectorRef, RoomSelectorProps>(({ 
  accessToken, 
  eventId, 
  currentRoomId,
  onRoomChange,
  cachedRooms = [],
  isLoadingRooms = false
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Register as overlay when panel or confirm dialog is open
  useOverlay('room-selector', isOpen || showConfirmDialog);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev)
  }));

  // Use cached rooms from parent, no need to fetch
  const rooms = cachedRooms;

  const handleRoomClick = (room: Room) => {
    if (room.room_id === currentRoomId) return; // Already in this room
    setSelectedRoom(room);
    setShowConfirmDialog(true);
  };

  const handleConfirmJoin = () => {
    if (selectedRoom) {
      onRoomChange(selectedRoom);
      setShowConfirmDialog(false);
      setSelectedRoom(null);
      setIsOpen(false);
    }
  };

  const handleCancelJoin = () => {
    setShowConfirmDialog(false);
    setSelectedRoom(null);
  };

  // Hardcoded call rooms that open in new tab
  const callRooms = [
    { id: 1, name: '[Call Room] 1', url: 'https://meet.google.com/wdg-nbqh-vxu' },
    { id: 2, name: '[Call Room] 2', url: 'https://meet.google.com/kgt-pgui-ide' },
    { id: 3, name: '[Call Room] 3', url: 'https://meet.google.com/ygj-pdpz-tan' },
  ];

  const handleCallRoomClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`px-4 py-2 rounded-lg transition-all duration-200 font-semibold flex items-center gap-2 ${
          isOpen
            ? 'bg-purple-700 bg-opacity-90 text-white'
            : 'bg-purple-600 bg-opacity-80 text-white hover:bg-opacity-100'
        }`}
        title="Room Selector"
      >
        <FaDoorOpen size={16} />
        Rooms
      </button>

      {/* Room Selector Panel */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-80 bg-[#1a1a1a] border border-[#333] rounded-lg shadow-2xl z-50 flex flex-col max-h-[70vh]"
          style={{ zIndex: 10001 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#333]">
            <h3 className="text-white font-semibold text-lg flex items-center gap-2">
              <FaDoorOpen />
              Room Selector
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-[#999] hover:text-white transition-colors"
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* Room List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {isLoadingRooms ? (
              <div className="text-center text-[#666] py-8">Loading rooms...</div>
            ) : rooms.length === 0 ? (
              <div className="text-center text-[#666] py-8">No rooms available</div>
            ) : (
              <>
                {/* Regular Rooms */}
                {rooms.map((room) => {
                  const isCurrentRoom = room.room_id === currentRoomId;
                  return (
                    <button
                      key={room.room_id}
                      onClick={() => handleRoomClick(room)}
                      disabled={isCurrentRoom}
                      className={`w-full p-3 rounded-lg text-left transition-all duration-200 ${
                        isCurrentRoom
                          ? 'bg-green-600 bg-opacity-30 border border-green-600 cursor-default'
                          : 'bg-[#2a2a2a] border border-[#444] hover:bg-[#333] hover:border-[#555] cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-white font-medium">{room.room_name}</div>
                        </div>
                        {isCurrentRoom && (
                          <FaCheck className="text-green-500" size={16} />
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Divider */}
                {rooms.length > 0 && (
                  <div className="border-t border-[#444] my-2"></div>
                )}

                {/* Call Rooms (External Links) */}
                {callRooms.map((callRoom) => (
                  <button
                    key={callRoom.id}
                    onClick={() => handleCallRoomClick(callRoom.url)}
                    className="w-full p-3 rounded-lg text-left transition-all duration-200 bg-[#2a2a2a] border border-[#444] hover:bg-blue-900 hover:bg-opacity-30 hover:border-blue-600 cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FaVideo className="text-blue-400" size={14} />
                        <div className="text-white font-medium">{callRoom.name}</div>
                      </div>
                      <FaExternalLinkAlt className="text-blue-400" size={12} />
                    </div>
                    <div className="text-xs text-[#666] mt-1 ml-6">
                      {callRoom.url.replace('https://', '')}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && selectedRoom && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          style={{ zIndex: 10002 }}
          onClick={handleCancelJoin}
        >
          <div
            className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-xl font-semibold mb-4">
              Join Room?
            </h3>
            <p className="text-[#999] mb-6">
              Are you sure you want to join <strong className="text-white">{selectedRoom.room_name}</strong>?
              <br />
              <span className="text-xs text-[#666] mt-2 block">
                This will disconnect you from your current room.
              </span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelJoin}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-lg hover:bg-[#333] transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmJoin}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200"
              >
                Join Room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

RoomSelector.displayName = 'RoomSelector';

export default RoomSelector;

